import { createElement, useState } from "react";
import { Trans } from "react-i18next";
import { DotLegalFile, useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { deleteHttp, put, sendWithFile } from "@common/api/apiShared";
import { useUploadProgress } from "@common/components/uploadProgress/useUploadProgress";
import { useDotLegalMutation, useDotLegalQuery, useDotLegalQueryClient } from "@common/hooks/dotLegalReactQuery";
import { RiskAssessmentDocumentSaveModel, RiskAssessmentDocumentUpdateModel, RiskAssessmentDocumentViewModel } from "./RiskDocumentationPane.types";

export enum RiskAssessmentDocumentationOption {
    Link = 0,
    Upload = 1,
}

export function useRiskDocumentationPane(riskAssessmentId: string, onDocumentsChanged: () => void) {
    const snackbar = useDotLegalSnackbar();
    const { translateString } = useTranslation();
    const queryClient = useDotLegalQueryClient();

    const [documentationOption, setDocumentationOption] = useState(RiskAssessmentDocumentationOption.Upload);
    const [link, setLink] = useState<string | undefined>(undefined);
    const { uploadPercentage, trackUploadProgress } = useUploadProgress();

    const url = `/riskAssessment/${riskAssessmentId}/documents`;
    const { isLoading, data, refetch } = useDotLegalQuery<Array<RiskAssessmentDocumentViewModel>>(url);

    const createMutation = useDotLegalMutation(
        (upload: { saveModel: RiskAssessmentDocumentSaveModel; file: File | undefined; onProgress: (percentage: number) => void }) =>
            sendWithFile("POST", url, upload.saveModel, upload.file, upload.onProgress)
    );
    const deleteMutation = useDotLegalMutation((documentId: string) => deleteHttp(`/riskAssessment/${riskAssessmentId}/documents/${documentId}`));
    const updateMutation = useDotLegalMutation(
        (model: { documentId: string; update: RiskAssessmentDocumentUpdateModel }) => put(`/riskAssessment/${model.documentId}/documents`, model.update),
        {
            // Optimistically patch the newest version's file name / link so the inline field keeps the user's edit
            // instead of snapping back to the server value between debounced keystrokes.
            onMutate: (model) => {
                queryClient.setQueryData<Array<RiskAssessmentDocumentViewModel>>(url, (old) =>
                    (old ?? []).map((doc) =>
                        doc.documentId === model.documentId
                            ? { ...doc, fileName: model.update.fileName ?? doc.fileName, link: model.update.link ?? doc.link }
                            : doc
                    )
                );
            },
        }
    );

    const refetchAndNotify = async () => {
        await refetch();
        onDocumentsChanged();
    };

    const createDocuments = async (files: Array<DotLegalFile> | undefined) => {
        if (!files || files.length === 0) return;

        // Concurrent uploads would overwrite each other's percentage, so the bar only tracks a single file and
        // stays indeterminate for several
        const onProgress = trackUploadProgress();
        const reportProgress = files.length === 1 ? onProgress : () => {};

        // The uploads are independent, so POST them concurrently rather than one round-trip at a time.
        await Promise.all(
            files.map((file) =>
                createMutation.mutateAsync({
                    saveModel: { fileName: file.fileName, link: undefined },
                    file: file.file,
                    onProgress: reportProgress,
                })
            )
        );

        await refetchAndNotify();

        if (files.length === 1) {
            snackbar.show(createElement(Trans, { i18nKey: "documentUploaded", values: { document: files[0].fileName } }));
        } else {
            snackbar.show(createElement(Trans, { i18nKey: "multipleDocumentsUploaded", values: { count: files.length } }));
        }
    };

    const uploadLink = async () => {
        if (!link) return;

        await createMutation.mutateAsync({ saveModel: { fileName: undefined, link }, file: undefined, onProgress: trackUploadProgress() });
        await refetchAndNotify();
        snackbar.show(createElement(Trans, { i18nKey: "linkUploaded", values: { link } }));
        setLink(undefined);
    };

    const removeDocument = async (documentId: string) => {
        const document = data?.find((d) => d.documentId === documentId);
        await deleteMutation.mutateAsync(documentId);
        await refetchAndNotify();
        snackbar.show(createElement(Trans, { i18nKey: "documentDeleted", values: { document: document?.fileName ?? document?.link } }));
    };

    const updateDocument = (documentId: string, update: RiskAssessmentDocumentUpdateModel) => {
        updateMutation.mutateAsync({ documentId, update });
    };

    return {
        documents: data,
        isLoading,
        documentsUploading: createMutation.isLoading,
        uploadPercentage,
        documentationOption,
        setDocumentationOption,
        link,
        setLink,
        createDocuments,
        uploadLink,
        removeDocument,
        updateDocument,
    };
}
